import {
  marketingCohortGroups,
  MARKETING_COHORT_LINE_HORIZON,
} from './marketingCohortsModel';
import {
  MarketingDeltaConfidence,
  marketingMetricConfidence,
} from './marketingCompareModel';
import {
  formatMarketingCount,
  formatMarketingMoney,
  formatMarketingPct,
  formatMarketingSales,
  IMarketingDelta,
  marketingBaseShare,
  marketingDeltaIsZero,
  marketingDeltaLabel,
  marketingMetricDelta,
  marketingMetricValue,
} from './marketingModel';
import { IMarketingCohortRow, IMarketingMetrics } from './types';

/**
 * ТЗ 0103, повторное ревью (пакет 2) — выводы одной фразой над экранами.
 *
 * Жалоба владельца: «как будто клонировали Google Analytics, а выводов не
 * сделать». Каждый экран за 5 секунд отвечает «что происходит»: одна фраза
 * сверху, собранная из ТЕХ ЖЕ чисел, что на экране (построитель получает
 * те же значения, что и плитки / таблица), и без выдумок: при малой базе или
 * без данных фраза так и говорит — «данных мало», «данных нет».
 *
 * Фраза — последовательность кусков: ключ перевода с параметрами или
 * знак препинания (запятая, тире, точка — одинаковы в ru и pl). Параметр
 * может быть вложенным куском («(−47%)» — `marketing.compare.delta`).
 * Модель ничего не переводит — рисует `MarketingInsight` страницы
 * (`marketingInsightText` с `t`), тесты — настоящим i18next с бандлами.
 */

export type MarketingInsightTone = 'good' | 'bad' | 'warn' | 'neutral' | 'info';

/** Кусок фразы: ключ перевода и подстановки (строки или вложенные куски). */
export interface IMarketingInsightPart {
  key: string;
  params?: Readonly<Record<string, MarketingInsightParam>>;
}

export type MarketingInsightParam = string | IMarketingInsightPart;

/** Кусок фразы или литерал (пунктуация, пробел). */
export type MarketingInsightSegment = string | IMarketingInsightPart;

export interface IMarketingInsight {
  tone: MarketingInsightTone;
  segments: ReadonlyArray<MarketingInsightSegment>;
}

/** Перевод ключа с подстановками — `t` из react-i18next или i18next. */
export type MarketingInsightTranslate = (
  key: string,
  params?: Record<string, string>,
) => string;

/** Текст одного куска (вложенные куски — рекурсивно). */
export function marketingInsightPartText(
  part: IMarketingInsightPart,
  t: MarketingInsightTranslate,
): string {
  if (!part.params) return t(part.key);
  const params: Record<string, string> = {};
  Object.entries(part.params).forEach(([name, value]) => {
    params[name] =
      typeof value === 'string' ? value : marketingInsightPartText(value, t);
  });
  return t(part.key, params);
}

/**
 * Фраза целиком: куски подряд, литералы — как есть. Литерал с точки после
 * куска, который сам кончается точкой («… 26,5 п. п.» + «.»), точку теряет:
 * иначе «п. п..» / «p.p..» (финальная проверка 0103, F-E3).
 */
export const marketingInsightText = (
  insight: Pick<IMarketingInsight, 'segments'>,
  t: MarketingInsightTranslate,
): string =>
  insight.segments.reduce<string>((text, segment) => {
    if (typeof segment !== 'string') {
      return text + marketingInsightPartText(segment, t);
    }
    if (segment.startsWith('.') && text.trimEnd().endsWith('.')) {
      return text + segment.slice(1);
    }
    return text + segment;
  }, '');

/** Кусок фразы короче: `part('marketing.x', { n: '3' })`. */
export const marketingInsightPart = (
  key: string,
  params?: Readonly<Record<string, MarketingInsightParam>>,
): IMarketingInsightPart => (params ? { key, params } : { key });

/**
 * Куски через разделитель: `[a, ', ', b, ', ', c]` — пустые (`null`)
 * выпадают вместе со своим разделителем.
 */
export const marketingInsightJoin = (
  items: ReadonlyArray<MarketingInsightSegment | null | undefined>,
  separator: string,
): MarketingInsightSegment[] =>
  items
    .filter((item): item is MarketingInsightSegment => Boolean(item))
    .flatMap((item, index) => (index ? [separator, item] : [item]));

/* ── Общие куски: дельта в скобках, как на плитке ─────────────────────── */

/**
 * Дельта в скобках « (−58,2%)» — та же подпись, что у плитки
 * (`marketingDeltaLabel`): без изменений — « (без изменений)», с нуля —
 * « (было 0)»; малая база или нет стороны — `null` (плитка там серая).
 */
export const marketingInsightDelta = (
  delta: IMarketingDelta | null,
  confidence: MarketingDeltaConfidence,
  lang?: string | null,
): IMarketingInsightPart | null => {
  if (!delta || confidence !== 'ok') return null;
  const label = marketingDeltaLabel(delta, lang);
  switch (label.key) {
    case 'marketing.delta.pct':
      return marketingInsightPart('marketing.insights.delta', {
        pct: label.pct ?? '',
      });
    case 'marketing.delta.points':
      return marketingInsightPart('marketing.insights.deltaPoints', {
        pct: label.pct ?? '',
      });
    case 'marketing.delta.same':
      return marketingInsightPart('marketing.insights.deltaSame');
    default:
      return marketingInsightPart('marketing.insights.deltaFromZero');
  }
};

/* ── «Лиды и продажи» › тренд ────────────────────────────────────────── */

export interface IMarketingLeadsInsightInput {
  /** Дней в окне (тот же период, что у плиток). */
  days: number;
  /** Итоги окна (как у плиток — `marketingDerivedMetrics`). */
  current: IMarketingMetrics | null | undefined;
  /** Итоги окна сравнения; `null` — сравнение выключено или не пришло. */
  previous: IMarketingMetrics | null | undefined;
  lang?: string | null;
  /**
   * Финальная проверка 0103 (F-E15): окно сравнения начинается раньше
   * истории CRM-лидов — у лидов (и продаж ÷ лиды) прошлого нет: без «(было
   * 0)», без п. п. и без вывода; продажи сравниваются как обычно.
   */
  leadsComparable?: boolean;
  /**
   * F-E2: есть устаревшие источники (замечание к данным) — тон не «хорошо»:
   * рост продаж может оказаться недосбором прошлого окна.
   */
  staleSources?: boolean;
}

/**
 * F-E2: вывод «лидов меньше / больше — продаж на один лид больше / меньше»
 * — только при заметных сдвигах: лидов на 5 % и больше, доли — на 1 п. п. и
 * больше; меньше — шум, о направлении фраза молчит.
 */
export const MARKETING_LEADS_REASON_MIN_PCT = 5;
export const MARKETING_LEADS_REASON_MIN_POINTS = 1;

/**
 * «За 30 дн.: CRM-лидов 88 (−47%), продаж 29 (+53%), продажи ÷ лиды выросли
 * на 21 п. п. — лидов меньше, но они покупают чаще.» Числа и дельты — те же,
 * что у плиток «CRM-лиды», «Продажи», «Продажи ÷ лиды периода»: малая база
 * (< 10 событий с обеих сторон) — без процента и с «данных мало для
 * сравнения»; без сравнения — без скобок и без вывода о направлении.
 */
export const marketingLeadsInsight = ({
  days,
  current,
  previous,
  lang,
  leadsComparable = true,
  staleSources = false,
}: IMarketingLeadsInsightInput): IMarketingInsight | null => {
  if (!current) return null;
  const leads = marketingMetricValue(current, 'crmLeads');
  const sales = marketingMetricValue(current, 'sales');
  if (leads === null) return null;
  const head = marketingInsightPart('marketing.insights.leads.head', {
    days: formatMarketingCount(days, lang),
  });
  if (leads === 0 && !sales) {
    return {
      tone: 'neutral',
      segments: [
        head,
        ' ',
        marketingInsightPart('marketing.insights.leads.empty'),
        '.',
      ],
    };
  }
  const compared = Boolean(previous);
  // F-E15: прошлое окно раньше истории CRM-лидов — лидовые дельты молчат.
  const leadsCompared = compared && leadsComparable;
  const prevLeads = marketingMetricValue(previous, 'crmLeads');
  const prevSales = marketingMetricValue(previous, 'sales');
  const leadsConfidence = leadsCompared
    ? marketingMetricConfidence('crmLeads', leads, prevLeads)
    : 'none';
  const salesConfidence = compared
    ? marketingMetricConfidence('sales', sales, prevSales)
    : 'none';
  const leadsDelta = leadsCompared
    ? marketingMetricDelta(current, previous, 'crmLeads')
    : null;
  const salesDelta = compared
    ? marketingMetricDelta(current, previous, 'sales')
    : null;
  const rateDelta = leadsCompared
    ? marketingMetricDelta(current, previous, 'leadToSale')
    : null;
  const smallBase =
    compared &&
    ((leadsCompared && leadsConfidence === 'smallBase') ||
      salesConfidence === 'smallBase');
  const reliable =
    leadsCompared && leadsConfidence === 'ok' && salesConfidence === 'ok';

  const leadsPart = marketingInsightPart('marketing.insights.leads.leads', {
    value: formatMarketingCount(leads, lang),
    delta: marketingInsightDelta(leadsDelta, leadsConfidence, lang) ?? '',
  });
  const salesPart = marketingInsightPart('marketing.insights.leads.sales', {
    value: formatMarketingSales(sales, lang),
    delta: marketingInsightDelta(salesDelta, salesConfidence, lang) ?? '',
  });
  let ratePart: IMarketingInsightPart | null = null;
  const points = rateDelta?.points;
  if (reliable && rateDelta && points !== undefined) {
    // Модуль п. п. — как на плитке, без знака: направление — словом.
    const abs = formatMarketingSales(Math.abs(points), lang);
    if (points > 0) {
      ratePart = marketingInsightPart('marketing.insights.leads.rateUp', {
        points: abs,
      });
    } else if (points < 0) {
      ratePart = marketingInsightPart('marketing.insights.leads.rateDown', {
        points: abs,
      });
    } else {
      ratePart = marketingInsightPart('marketing.insights.leads.rateSame');
    }
  } else {
    const rate = marketingMetricValue(current, 'leadToSale');
    if (rate !== null) {
      ratePart = marketingInsightPart('marketing.insights.leads.rateValue', {
        value: formatMarketingPct(rate, lang),
      });
    }
  }

  // Вывод о направлении — только на надёжных и заметных дельтах (F-E2):
  // «продажи ÷ лиды» — не когорта (продажи окна — и от лидов прошлых
  // окон), поэтому вывод — только когда доля в обоих окнах не больше 100 %,
  // лидов изменилось на 5 % и больше, доля — на 1 п. п. и больше; словами —
  // «продаж на один лид стало больше / меньше», а не «покупают чаще».
  const rateNow = marketingMetricValue(current, 'leadToSale');
  const ratePrev = marketingMetricValue(previous, 'leadToSale');
  const sharesSane =
    rateNow !== null && ratePrev !== null && rateNow <= 1 && ratePrev <= 1;
  let reason: IMarketingInsightPart | null = null;
  if (
    reliable &&
    sharesSane &&
    leadsDelta &&
    !marketingDeltaIsZero(leadsDelta) &&
    leadsDelta.pct !== null &&
    Math.abs(leadsDelta.pct) >= MARKETING_LEADS_REASON_MIN_PCT &&
    points !== undefined &&
    Math.abs(points) >= MARKETING_LEADS_REASON_MIN_POINTS
  ) {
    const more = leadsDelta.diff > 0;
    const better = points > 0;
    let key = 'marketing.insights.leads.reason.fewerAndWorse';
    if (more && better) key = 'marketing.insights.leads.reason.moreAndBetter';
    if (more && !better) key = 'marketing.insights.leads.reason.moreButWorse';
    if (!more && better) key = 'marketing.insights.leads.reason.fewerButBetter';
    reason = marketingInsightPart(key);
  }
  if (smallBase) {
    reason = marketingInsightPart('marketing.insights.smallBase');
  }

  let tone: MarketingInsightTone = 'info';
  if (smallBase) tone = 'neutral';
  else if (reliable && salesDelta && !marketingDeltaIsZero(salesDelta)) {
    tone = salesDelta.diff > 0 ? 'good' : 'bad';
  }
  // F-E2: при устаревших источниках «хорошо» не говорим — нейтрально.
  if (tone === 'good' && staleSources) tone = 'neutral';
  return {
    tone,
    segments: [
      head,
      ' ',
      ...marketingInsightJoin([leadsPart, salesPart, ratePart], ', '),
      ...(reason ? [' — ', reason] : []),
      '.',
    ],
  };
};

/* ── «Лиды и продажи» › когорты ──────────────────────────────────────── */

/**
 * «Через 30 дней покупают ≈ 10,3% лидов (по полным неделям, лидов: 120).»
 * Та же доля, что у матрицы когорт на 30-м дне: продажи ÷ лиды только
 * ЗРЕЛЫХ когорт (`marketingCohortGroups`), меньше 30 лидов в базе —
 * «данных мало», полных когорт нет — так и сказано.
 */
export const marketingCohortsInsight = (
  rows: ReadonlyArray<IMarketingCohortRow> | null | undefined,
  granularity: 'WEEK' | 'MONTH' | string,
  lang?: string | null,
): IMarketingInsight | null => {
  if (!rows) return null;
  const days = MARKETING_COHORT_LINE_HORIZON;
  const sums = marketingCohortGroups(rows).reduce(
    (acc, group) => ({
      sales: acc.sales + group.horizons[days].sales,
      leads: acc.leads + group.horizons[days].leads,
    }),
    { sales: 0, leads: 0 },
  );
  const daysText = formatMarketingCount(days, lang);
  if (sums.leads <= 0) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.cohorts.none', {
          days: daysText,
        }),
      ],
    };
  }
  const share = marketingBaseShare(sums.sales, sums.leads);
  const leadsText = formatMarketingCount(sums.leads, lang);
  if (share === null) {
    return {
      tone: 'neutral',
      segments: [
        marketingInsightPart('marketing.insights.cohorts.smallBase', {
          days: daysText,
          n: leadsText,
        }),
      ],
    };
  }
  return {
    tone: 'info',
    segments: [
      marketingInsightPart('marketing.insights.cohorts.share', {
        days: daysText,
        pct: formatMarketingPct(share, lang),
        basis: marketingInsightPart(
          granularity === 'MONTH'
            ? 'marketing.insights.cohorts.months'
            : 'marketing.insights.cohorts.weeks',
          { n: leadsText },
        ),
      }),
    ],
  };
};

/* ── «Сегодня» ───────────────────────────────────────────────────────── */

export interface IMarketingTodayInsightInput {
  /** Карточек с важностью critical (как в вердикте). */
  urgent: number;
  /** Расход впустую (`marketingTodayVerdict.lost`). */
  lost: number;
  /** Сущность, на которую приходится больше всего «впустую»; `null` — нет. */
  topName?: string | null;
  /**
   * Её «впустую» (`marketingTodayLostLeader`): больше половины суммы —
   * «в основном «X»», иначе — «больше всего — «X»» (F-E8); не задано —
   * «больше всего».
   */
  topLost?: number | null;
  /**
   * Окно денег в днях, если оно у всех карточек «впустую» одно (поле
   * сервера); `null` — окна разные или неизвестны: без «за N дн.».
   */
  windowDays?: number | null;
  lang?: string | null;
}

/**
 * «Сегодня: срочно 2. За 7 дн. впустую ≈ 712 PLN — в основном
 * «Widoczni[Pmax] Cybersecurity».» (лидер — больше половины суммы; иначе
 * «…, больше всего — «X».») Те же числа, что у секций: срочные —
 * счётчик карточек critical, «впустую» — сумма карточек «Деньги уходят
 * впустую» (сущность — один раз). Потенциал SEO сюда не входит.
 */
export const marketingTodayInsight = ({
  urgent,
  lost,
  topName = null,
  topLost = null,
  windowDays = null,
  lang,
}: IMarketingTodayInsightInput): IMarketingInsight => {
  const segments: MarketingInsightSegment[] = [
    urgent > 0
      ? marketingInsightPart('marketing.insights.today.urgent', {
          n: formatMarketingCount(urgent, lang),
        })
      : marketingInsightPart('marketing.insights.today.calm'),
  ];
  if (lost > 0) {
    const value = formatMarketingMoney(lost, lang);
    segments.push(
      ' ',
      windowDays
        ? marketingInsightPart('marketing.insights.today.lostWindow', {
            value,
            days: formatMarketingCount(windowDays, lang),
          })
        : marketingInsightPart('marketing.insights.today.lost', { value }),
    );
    if (topName) {
      // Финальная проверка 0103 (F-E8): «в основном» — только когда на
      // лидера приходится больше половины «впустую».
      const mostly = topLost !== null && topLost > lost / 2;
      segments.push(
        mostly ? ' — ' : ', ',
        marketingInsightPart(
          mostly
            ? 'marketing.insights.today.mostly'
            : 'marketing.insights.today.most',
          { name: topName },
        ),
      );
    }
    segments.push('.');
  }
  let tone: MarketingInsightTone = 'good';
  if (lost > 0) tone = 'warn';
  if (urgent > 0) tone = 'bad';
  return { tone, segments };
};
